import {
  campaignMediums,
  campaignStates,
  campaignMediumTextFromValue,
  campaignStateTextFromValue,
} from 'utils/campaigns';
import { deepMergeNew, generateId, omitFromObject } from 'utils/helpers';
import { getMetricValue } from 'utils/metrics';
import { relativeRangeToAbsolute } from 'utils/relativeDateTime';
import { visualizationDef } from './visualizations';
import * as widgetTypes from './dashboardWidgetTypes';
import * as dataTypes from './dataTypes';

export const NULL_METRIC = { name: '', enumName: 'nullMetric' };

/**
 * Removes NULL_METRIC from widget settings.
 *
 * ```
 * filterNullMetricFromSettings(settings)
 * ```
 * @param { Object } settings: The widget settings containing the metric object.
 * @returns { Object } Filtered settings.
 */
export const filterNullMetricFromSettings = (settings = {}) => {
  const { metric, ...otherSettings } = settings;
  if (metric && metric.enumName === NULL_METRIC.enumName) {
    return { ...otherSettings };
  }
  return settings;
};

/**
 * Get widget type from a widget object
 *
 * ```
 * getWidgetType(widget)
 * ```
 * @param { Object } widget: The widget config.
 * @returns { Object } The type of the widget.
 */
export const getWidgetType = (widget = {}) => widget.type || widgetTypes.METRIC;

/**
 * Get settings from a widget object
 *
 * ```
 * getWidgetSettings(widget)
 * ```
 * @param { Object } widget: The widget config.
 * @returns { Object } The settings of the widget.
 */
export const getWidgetSettings = (widget) => {
  const { settings } = widget || {
    settings: { fetchParams: { page: 1 }, metric: NULL_METRIC, options: [], visualization: '' },
  };
  return settings;
};

/**
 * Get fetch params from a widget object
 *
 * ```
 * getWidgetFetchParams(widget)
 * ```
 * @param { Object } widget: The widget config.
 * @param { Object } data: Additional fetch data (Used for campaigns).
 * @returns { Object } The fetch params of the widget.
 */
export const getWidgetFetchParams = (widget, data) => {
  const { fetchParams } = data || getWidgetSettings(widget);
  return fetchParams || {};
};

/**
 * Filter specific fetch params of a value from a certain key.
 *
 * ```
 * filterFetchParam(fetchParams, key, value)
 * ```
 * @param { Object } fetchParams: The fetch params.
 * @param { String } key: The key to compare.
 * @param { String } valueToOmit: The value to filter.
 * @returns { Object } The filtered fetch params.
 */
export const filterFetchParam = (fetchParams, key, valueToOmit) => {
  const value = fetchParams[key];
  if (value === valueToOmit) {
    return omitFromObject(fetchParams, key);
  }
  return fetchParams;
};

/**
 * Campaigns table widget medium and state.
 *
 * ```
 * getCampaignWidgetFilterString(widget)
 * ```
 * @param { Object } widget: The campaign table wiget.
 * @returns { String } The medium and state of campaign widget.
 */
export const getCampaignWidgetFilterString = (widget) => {
  const All = 'All Campaigns';
  const { mediumFilter, stateFilter } = getWidgetFetchParams(widget);
  if (mediumFilter || stateFilter) {
    const medium = mediumFilter === campaignMediums.ALL.value ? '' : mediumFilter;
    const mediumText = campaignMediumTextFromValue[medium];
    const mediumString = mediumText ? `Medium: ${mediumText}` : '';

    const state = stateFilter === campaignStates.ALL.value ? '' : stateFilter;
    const stateText = campaignStateTextFromValue[state];
    const stateLabel = stateText ? `State: ${stateText}` : '';
    const stateString = medium && state ? `, ${stateLabel}` : stateLabel;

    if (mediumString || stateString) {
      return `Campaigns (${mediumString}${stateString})`;
    }
    return All;
  }
  return All;
};

/**
 * Get metric from a widget object
 *
 * ```
 * getWidgetMetric(widget)
 * ```
 * @param { Object } widget: The widget config.
 * @returns { Object } The metric of the widget.
 */
export const getWidgetMetric = (widget) => {
  const { metric } = getWidgetSettings(widget) || { metric: NULL_METRIC };
  return metric || NULL_METRIC;
};

/**
 * Get visualization from a widget object
 *
 * ```
 * getWidgetVisualization(widget)
 * ```
 * @param { Object } widget: The widget config.
 * @returns { String } The visualization of the widget.
 */
export const getWidgetVisualization = (widget) => {
  const { visualization } = getWidgetSettings(widget) || { visualization: '' };
  return visualization;
};

/**
 * Get data identifier of widget
 *
 * ```
 * getWidgetDataIdentifier(widget)
 * ```
 * @param { Object } widget: The widget config.
 * @returns { Object } The widget data identifier of the widget.
 */
export const getWidgetDataIdentifier = (widget) => {
  const widgetType = getWidgetType(widget);
  switch (widgetType) {
    case widgetTypes.CAMPAIGNS_TABLE:
      return widget.id;
    case widgetTypes.METRIC:
    default:
      return getMetricValue(getWidgetMetric(widget));
  }
};

/**
 * Get dataType of widget
 *
 * ```
 * getWidgetDataType(widget)
 * ```
 * @param { Object } widget: The widget config.
 * @returns { Object } The dataType of the widget.
 */
export const getWidgetDataType = (widget) => {
  const settings = getWidgetSettings(widget);
  const visualization = getWidgetVisualization(widget);
  const { getDataType } = visualizationDef[visualization.toLowerCase()] || {
    getDataType: () => 'series',
  };
  return getDataType(settings);
};

/**
 * If widget is a metric widget
 *
 * ```
 * isMetricWidget(widget)
 * ```
 * @param { Object } widget: The widget.
 * @returns { Boolean } If the widget is a metric widget.
 */
export const isMetricWidget = (widget) => getWidgetType(widget) === widgetTypes.METRIC;

/**
 * If widget is a campaigns table widget
 *
 * ```
 * isCampaignsTableWidget(widget)
 * ```
 * @param { Object } widget: The widget.
 * @returns { Boolean } If the widget is a campaigns table widget.
 */
export const isCampaignsTableWidget = (widget) =>
  getWidgetType(widget) === widgetTypes.CAMPAIGNS_TABLE;

/**
 * Create a dashboard data object for a widget.
 *
 * ```
 * createDashboardDataObject(widget, data)
 * ```
 * @param { Object } widget: Object data of widget.
 * @param { Object } data: Widget data.
 * @returns { Object } The dashboard data object of widget.
 */
export const createDashboardDataObject = (widget, data) => ({
  [getWidgetDataType(widget)]: {
    [getWidgetDataIdentifier(widget)]: data,
  },
});

/**
 * Create the widgetData object from layout array and widgets object.
 *
 * ```
 * createWidgetData(layout)
 * ```
 * @param { Array } layout: The layout of widgets in the dashboard.
 * @param { Object } widgets: Object of widgets.
 * @param { Object } campaignFetchParamData: Fetch params for campaigns.
 * @returns { Array } The widgetData array.
 */
export const createWidgetData = (layout, widgets, campaignFetchParamData) =>
  layout.map(({ w, h, x, y, i }) => {
    const { settings, name, type } = widgets[i];
    const filteredSettings = filterNullMetricFromSettings(settings);
    let widgetSettings = { ...filteredSettings };

    if (type === widgetTypes.CAMPAIGNS_TABLE) {
      // For campaigns table, save fetch params from campaignFetchParamData state instead since the
      // data for fetchParams in widgetData is not updated to prevent reloading of all widgets.
      widgetSettings = deepMergeNew(filteredSettings, { fetchParams: campaignFetchParamData[i] });
    }
    return { w, h, x, y, settings: { ...widgetSettings, name }, type };
  });

/**
 * Create the layout array and widgets object from widgetData.
 *
 * ```
 * createLayoutAndWidgets(widgetData)
 * ```
 * @param { Array } widgetData: Array of widget data.
 * @param { Object } fetchParams: Additional fetch params for widgets (campaigns).
 * @returns { Object } Object containing layout and widgets.
 */
export const createLayoutAndWidgets = (widgetData, fetchParams) => {
  const campaignFetchParamData = {};
  const layout = [];
  const widgets = {};

  widgetData.forEach((widget) => {
    const { x, y, w, h, i } = widget;
    const dataType = getWidgetDataType(widget);
    const id = i || generateId();
    const settings = getWidgetSettings(widget);

    // Setting a min height and width of 2 rows / columns for all widgets for better sizing
    // precision but also preventing widgets from being too small.
    layout.push({ minH: 2, minW: 2, i: id, x, y, w, h });

    if (dataType === dataTypes.CAMPAIGNS) {
      campaignFetchParamData[id] = { ...fetchParams };
    }
    widgets[id] = { ...widget, id, dataType, name: settings.name };
  });
  return { layout, widgets };
};

/**
 * Formats the data response when fetching dashboard data.
 *
 * ```
 * formatWidgetDataResponse(widgetData, widgetSettings, startRange, endRange)
 * ```
 * @param { Array } widgetData: Array of widget data.
 * @param { Object } widgetSettings: Object of widget settings.
 * @param { Moment } startRange: The start range time of the dashboard.
 * @param { Moment } endRange: The end range time of the dashboard.
 * @returns { Object } Object containing formatted response.
 */
export const formatWidgetDataResponse = (widgetData, widgetSettings, startRange, endRange) => {
  const { defaultStartRange, defaultEndRange, fetchParams, theme = 'default' } = widgetSettings;

  const widgetList = widgetData || [];
  const { campaignFetchParamData, layout, widgets } = createLayoutAndWidgets(
    widgetList,
    fetchParams,
  );

  return {
    campaignFetchParamData,
    defaultStartRange,
    defaultEndRange,
    layout,
    theme,
    widgets,
    ...relativeRangeToAbsolute(defaultStartRange || startRange, defaultEndRange || endRange),
  };
};
